import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import { setDateRangePicTreeNttFt, setDateRangePicTreeMinMax, setSelectedSeasonNttFt, getSeasonDataNttFt } from "actions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const NTT_FT_OITA_SEASON_ID = "cmvto6100000108phdaocdk18";
export const NTT_FT_OKINAWA_SEASON_ID = "cmkdxxnxn000141s4asb2gdfv";
export const NTT_FT_SEASON_OPTIONS = [
  { name: "大分シーズン", value: NTT_FT_OITA_SEASON_ID },
  { name: "沖縄シーズン", value: NTT_FT_OKINAWA_SEASON_ID },
];

export const getClampedSeasonRange = (startValue, endValue, now = moment().tz("Asia/Tokyo")) => {
  const seasonStart = moment(startValue).format("YYYY-MM-DD");
  const seasonEnd = moment(endValue).format("YYYY-MM-DD");
  const today = now.format("YYYY-MM-DD");
  if (moment(today).isBefore(seasonStart)) return [seasonStart, seasonStart];
  if (moment(today).isAfter(seasonEnd)) return [seasonStart, seasonEnd];
  return [seasonStart, today];
};

export const getActiveSeason = (seasons, now = moment().tz("Asia/Tokyo")) => (
  (seasons || []).find((season) => {
    const start = moment.tz(season.seasonStart, "YYYY-MM-DD HH:mm", "Asia/Tokyo");
    const end = moment.tz(season.seasonEnd, "YYYY-MM-DD HH:mm", "Asia/Tokyo");
    return now.isSameOrAfter(start) && now.isSameOrBefore(end);
  })
);

export const getDefaultSeason = (seasons, now = moment().tz("Asia/Tokyo")) => {
  const activeSeason = getActiveSeason(seasons, now);
  if (activeSeason) return activeSeason;
  return (seasons || [])
    .filter((season) => {
      const start = moment.tz(season.seasonStart, "YYYY-MM-DD HH:mm", "Asia/Tokyo");
      return now.isSameOrAfter(start);
    })
    .sort((a, b) => (
      moment(b.seasonStart).valueOf() - moment(a.seasonStart).valueOf()
    ))[0];
};

export const isValidSeasonDateRange = (
  dateRange,
  seasonStart,
  seasonEnd,
  now = moment().tz("Asia/Tokyo")
) => {
  if (!Array.isArray(dateRange) || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
    return false;
  }
  const start = moment(dateRange[0]).format("YYYY-MM-DD");
  const end = moment(dateRange[1]).format("YYYY-MM-DD");
  const min = moment(seasonStart).format("YYYY-MM-DD");
  const seasonStartMoment = moment(seasonStart).startOf("day");
  const seasonEndMoment = moment(seasonEnd).startOf("day");
  const max = now.isBefore(seasonStartMoment)
    ? seasonEndMoment.format("YYYY-MM-DD")
    : moment.min(seasonEndMoment, now.clone().startOf("day")).format("YYYY-MM-DD");
  return start <= end && start >= min && end <= max;
};

const sameDateRange = (left, right) => (
  Array.isArray(left)
  && Array.isArray(right)
  && left.length === 2
  && right.length === 2
  && left[0] === right[0]
  && left[1] === right[1]
);

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
    singleSeasonWrapper: {
      width: "auto",
      display: "inline-block",
      marginTop: -24,
    },
  })
);

const PeriodNttFt = ({
  setDateRangePicTreeNttFt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNttFt,
  getSeasonDataNttFt,
  seasonData,
  selectedSeason,
  dateRangePicTreeNttFt,
  dateRangePicTreeMinMax,
  singlePeriod,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const [seasonOption, setSeasonOption] = useState(NTT_FT_SEASON_OPTIONS[1]);
  const lastSeasonIdRef = useRef(null);

  useEffect(() => {
    getSeasonDataNttFt();
  }, [getSeasonDataNttFt]);

  useEffect(() => {
    if (seasonData.length > 0 && singlePeriod) {
      const selectedId = selectedSeason && selectedSeason[0] && selectedSeason[0].seasonId;
      const selectedRow = seasonData.find((season) => season.seasonId === selectedId);
      const season = selectedRow || getDefaultSeason(seasonData);
      if (!season) return;
      const nextOption = NTT_FT_SEASON_OPTIONS.find((option) => option.value === season.seasonId);
      if (!nextOption) return;
      if (seasonOption.value !== nextOption.value) setSeasonOption(nextOption);
      if (!selectedRow || selectedId !== season.seasonId) {
        setSelectedSeasonNttFt([season]);
      }
      const seasonChanged =
        lastSeasonIdRef.current !== null && lastSeasonIdRef.current !== season.seasonId;
      const hasValidRange = isValidSeasonDateRange(
        dateRangePicTreeNttFt,
        season.seasonStart,
        season.seasonEnd
      );
      const hasValidMinMax = isValidSeasonDateRange(
        dateRangePicTreeMinMax,
        season.seasonStart,
        season.seasonEnd
      );
      if (seasonChanged || !hasValidRange || !hasValidMinMax) {
        const [rangeStart, rangeEnd] = getClampedSeasonRange(season.seasonStart, season.seasonEnd);
        const nextRange = [rangeStart, rangeEnd];
        if (!sameDateRange(dateRangePicTreeMinMax, nextRange)) {
          setDateRangePicTreeMinMax(nextRange);
        }
        if (!sameDateRange(dateRangePicTreeNttFt, nextRange)) {
          setDateRangePicTreeNttFt(nextRange);
        }
      }
      lastSeasonIdRef.current = season.seasonId;
      return;
    }
    if (seasonData.length > 0) {
      const matchedSeasons = seasonData.filter((season) => {
        const startDate = season.seasonStart;
        const endDate = season.seasonEnd;
        return today >= startDate && today <= endDate;
      });
      if (!selectedSeason || selectedSeason.length === 0) {
        if (matchedSeasons.length > 0) {
          setSelectedSeasonNttFt(matchedSeasons);
          const earliestStart = matchedSeasons
            .map((s) => s.seasonStart)
            .reduce((a, b) => (a < b ? a : b));
          const latestEnd = matchedSeasons
            .map((s) => s.seasonEnd)
            .reduce((a, b) => (a > b ? a : b));

          const [rangeStart, rangeEnd] = getClampedSeasonRange(
            earliestStart,
            latestEnd
          );
          setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
          setDateRangePicTreeNttFt([rangeStart, rangeEnd]);
        } else {
          const defaultSeason = getDefaultSeason(seasonData);
          if (!defaultSeason) return;
          setSelectedSeasonNttFt([defaultSeason]);
          const [rangeStart, rangeEnd] = getClampedSeasonRange(
            defaultSeason.seasonStart,
            defaultSeason.seasonEnd
          );
          const nextRange = [rangeStart, rangeEnd];
          if (!sameDateRange(dateRangePicTreeNttFt, nextRange)) {
            setDateRangePicTreeNttFt(nextRange);
          }
          if (!sameDateRange(dateRangePicTreeMinMax, nextRange)) {
            setDateRangePicTreeMinMax(nextRange);
          }
        }
      }
    }
  }, [
    seasonData,
    selectedSeason,
    singlePeriod,
    seasonOption.value,
    setDateRangePicTreeMinMax,
    setDateRangePicTreeNttFt,
    setSelectedSeasonNttFt,
    today,
    dateRangePicTreeMinMax,
    dateRangePicTreeNttFt,
  ]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));

      const [rangeStart, rangeEnd] = getClampedSeasonRange(
        earliestStart,
        latestEnd
      );
      setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
      setDateRangePicTreeNttFt([rangeStart, rangeEnd]);

      // ステートを更新
      setSelectedSeasonNttFt(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTreeNttFt([]);
    }
  };

  const handleChange = (event) => {
    const nextOption = NTT_FT_SEASON_OPTIONS.find((option) => option.value === event.target.value);
    const nextSeason = seasonData.find((season) => season.seasonId === event.target.value);
    if (!nextOption || !nextSeason) return;
    setSeasonOption(nextOption);
    setSelectedSeasonNttFt([nextSeason]);
    const [rangeStart, rangeEnd] = getClampedSeasonRange(nextSeason.seasonStart, nextSeason.seasonEnd);
    setDateRangePicTreeNttFt([rangeStart, rangeEnd]);
    setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1} style={{marginTop: 8}}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div className={classes.singleSeasonWrapper}>
                <SingleSelect
                  selected={seasonOption}
                  options={NTT_FT_SEASON_OPTIONS}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

PeriodNttFt.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeNttFt.seasonData,
  selectedSeason: state.pictreeNttFt.selectedSeason,
  dateRangePicTreeNttFt: state.page.dateRangePicTreeNttFt,
  dateRangePicTreeMinMax: state.page.dateRangePicTreeMinMax,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeNttFt: (param) => dispatch(setDateRangePicTreeNttFt(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataNttFt: () => dispatch(getSeasonDataNttFt()),
  setSelectedSeasonNttFt: (param) => dispatch(setSelectedSeasonNttFt(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodNttFt));
